import type { ChoiceGroupProps, ChoiceGroupPropOnChange, ChoiceGroupDefaultItem, Direction } from 'uikit';
import { ChoiceGroup, Tooltip } from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import cn from 'classnames';
import { useCallback, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

type IControlledChoiceProps = Omit<ChoiceGroupProps, 'onChange' | 'multiple'> &
    Pick<UseControllerProps<any>, 'control' | 'rules'> & {
        required?: boolean;
        textLabel?: string;
        textLabelClassName?: string;
        textLabelPosition?: 'left' | 'top';
        isTooltipHidden?: boolean;
        onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
        classNameTooltip?: string;
        direction?: Direction;
        customStaticTooltip?: boolean;
        classChoiceGroup?: string;
    };

export const ControlledChoice = ({
    name,
    control,
    rules,
    required,
    textLabel,
    textLabelClassName,
    textLabelPosition = 'left',
    customStaticTooltip = false,
    size = 's',
    view = 'secondary',
    width = 'full',
    isTooltipHidden,
    onTooltipAttributeChange,
    classNameTooltip,
    classChoiceGroup,
    direction = 'rightCenter',
    ...choiceGroupProps
}: IControlledChoiceProps) => {
    const ref = useRef<HTMLDivElement>(null);

    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const handleOnChange = useCallback<ChoiceGroupPropOnChange<ChoiceGroupDefaultItem, false>>(
        ({ value }) => {
            field.onChange(value);
        },
        [field],
    );

    const tooltipRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    return (
        <>
            <div
                ref={ref}
                className={`TextField TextField_labelPosition_${textLabelPosition} TextField_size_${size} TextField_view_default TextField_width_full`}
            >
                {textLabel && (
                    <div
                        className={cn(
                            `Text Text_lineHeight_${size} Text_size_${size} Text_view_${view} FieldLabel TextField-Label TextField-Label_labelPosition_${textLabelPosition}`,
                            textLabelClassName,
                        )}
                    >
                        {textLabel}
                        {required && <span className="FieldLabel-Star">*</span>}
                    </div>
                )}
                <ChoiceGroup
                    name={name}
                    value={field.value}
                    onChange={handleOnChange}
                    size={size}
                    view={view}
                    multiple={false}
                    width={width}
                    className={classChoiceGroup}
                    {...choiceGroupProps}
                />
                {!isTooltipHidden && fieldState.invalid && fieldState.error?.message && customStaticTooltip && (
                    <div className="customStaticTooltip">{fieldState.error?.message}</div>
                )}
            </div>
            {!isTooltipHidden && fieldState.invalid && fieldState.error?.message && !customStaticTooltip && (
                <Tooltip
                    size="s"
                    direction={direction}
                    status="alert"
                    anchorRef={ref}
                    ref={tooltipRef}
                    className={`${classNameTooltip} tooltip-${name}`}
                >
                    {fieldState.error?.message}
                </Tooltip>
            )}
        </>
    );
};

ControlledChoice.displayName = 'ControlledChoice';
